import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { api } from "./api";
import { ErrorBanner, StepCard } from "./formParts";
import { DocumentChecklist, slotKey } from "./DocumentsStep";
import { Icon, T, cssVars, signupTheme, useSignupFonts } from "./signupDesign";
import PartnerHeader from "./PartnerHeader";

/**
 * Opened on the applicant's phone from the QR code on the Documents step:
 * /partner-signup/upload?u=<token>. The token can only list and add
 * documents for one application, for 30 minutes — nothing else in the
 * application is visible here.
 */
export default function MobileUpload() {
  const location = useLocation();
  const uploadToken = new URLSearchParams(location.search).get("u");
  const [view, setView] = useState(null);
  const [groups, setGroups] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [error, setError] = useState(null);
  const [busySlot, setBusySlot] = useState(null);
  const [lastUpload, setLastUpload] = useState(null);
  useSignupFonts();

  const load = useCallback(() => {
    if (!uploadToken) {
      setLoadError("This link is missing its code. Scan the QR code on your computer again.");
      return;
    }
    api("GET", "/mobile/checklist", { uploadToken })
      .then(setView)
      .catch((e) => setLoadError(e.message));
  }, [uploadToken]);

  useEffect(() => {
    load();
    api("GET", "/meta").then((m) => setGroups(m.documentGroups || null)).catch(() => {});
  }, [load]);

  const upload = async (slot, file) => {
    setError(null);
    setLastUpload(null);
    if (file.size > 15 * 1024 * 1024) {
      setError("That file is over 15 MB.");
      return;
    }
    setBusySlot(slotKey(slot));
    try {
      const form = new FormData();
      form.append("kind", slot.kind);
      if (slot.ownerIndex != null) form.append("ownerIndex", String(slot.ownerIndex));
      form.append("file", file, file.name);
      setView(await api("POST", "/mobile/documents", { uploadToken, form }));
      setLastUpload(slot.label);
    } catch (e) {
      if (e.status === 401) setLoadError(e.message);
      else setError(e.message);
    } finally {
      setBusySlot(null);
    }
  };

  return (
    <Box sx={{ background: T.pageBg, minHeight: "100vh" }}>
      <ThemeProvider theme={signupTheme}>
      <PartnerHeader />
      <Box sx={{ ...cssVars, fontFamily: T.body, color: T.text, maxWidth: 720, mx: "auto", px: 2, pt: { xs: "88px", md: "112px" }, pb: 6 }}>
        {loadError ? (
          <StepCard title="Upload documents">
            <Alert severity="warning" icon={<Icon.Alert size={20} />}>{loadError}</Alert>
          </StepCard>
        ) : !view ? (
          <Box sx={{ textAlign: "center", py: 8 }}><CircularProgress sx={{ color: T.red }} /></Box>
        ) : (
          <StepCard
            title="Upload documents"
            subtitle={`For ${view.restaurantName}. Take a photo or choose a file for each document; it appears on your computer as soon as it's uploaded.`}
          >
            <ErrorBanner error={error} />
            {lastUpload && <Alert severity="success" icon={<Icon.Check size={20} />} sx={{ mb: 2 }}>Uploaded: {lastUpload}</Alert>}
            <DocumentChecklist
              checklist={view.checklist}
              documents={view.documents}
              groups={groups}
              busySlot={busySlot}
              disabled={false}
              locked
              onUpload={upload}
            />
            <Typography sx={{ color: T.hint, fontSize: 14, mt: 2, textAlign: "center" }}>
              When you're done, close this page and carry on on your computer.
            </Typography>
          </StepCard>
        )}
      </Box>
      </ThemeProvider>
    </Box>
  );
}
